
import type { PropType } from 'vue'
import type { SearchModeEnum } from '../../enums/search-mode.enum'
import type { RegularFilter } from '../../models'

import { ExtraParams } from '@empathyco/x-components/extra-params'

import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'

import { mapGetters, useInstance } from '../../composables'
import { CONTAINER } from '../../config'

import { TYPES } from '../../config/inversify.types'
import { AUTH_CONSTS } from '../../store/auth/auth.consts'
import { CONTEXT_CONSTS } from '../../store/context/context.consts'
import { FiltersUtils } from '../../utils'

export default defineComponent({
  name: 'XExtraParams',
  components: {
    ExtraParams,
  },
  props: {
    endpointId: {
      type: String,
      default: 'search',
    },
    context: {
      type: String,
      default: 'query',
    },
    filters: {
      type: Object as PropType<Record<string, RegularFilter>>,
      required: true,
    },
    debug: {
      type: Boolean,
      default: false,
    },
    raw: {
      type: Object,
      default: null,
    },
    mode: {
      type: String as PropType<
        SearchModeEnum.KEYWORD | SearchModeEnum.HYBRID | SearchModeEnum.SEMANTICS
      >,
      default: undefined,
    },
  },
  setup(props) {
    const {
      proxy: { $store },
    } = getCurrentInstance()

    const { instanceConfig } = useInstance()

    const { context } = mapGetters($store, CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context,
    })

    const { token } = mapGetters($store, AUTH_CONSTS.moduleName, {
      token: AUTH_CONSTS.getters.token,
    })

    const filtersValue = computed<Record<string, string | Array<string>>>(() =>
      Object.values(props.filters).reduce<Record<string, string | Array<string>>>(
        (result, filter) => {
          if (filter.selectedValues.length) {
            const encodedFilterId = FiltersUtils.getEncodedId(filter.config)
            const encodedFilterSelectedValues = filter.selectedValues.map(filterValue =>
              FiltersUtils.encodeValue(filter.config, filterValue),
            )
            result[encodedFilterId] = !instanceConfig.value.allowMultipleValuesInSearchFilters
              ? encodedFilterSelectedValues[0]
              : encodedFilterSelectedValues
          }
          return result
        },
        {},
      ),
    )

    const values = ref({
      instanceId: context.value.instanceId,
      endpointId: props.endpointId,
      context: props.context,
      token: token.value,
      debug: props.debug,
      mode: props.mode,
      filters: filtersValue.value,
      raw: props.raw,
    })

    watch(
      () => [props, context, token, filtersValue.value],
      async () => {
        values.value.instanceId = context.value.instanceId
        values.value.endpointId = props.endpointId
        values.value.context = props.context
        values.value.token = token.value
        values.value.debug = props.debug
        values.value.mode = props.mode
        values.value.filters = filtersValue.value
        const extraParamsMapper = CONTAINER.get<Record<string, any>>(
          TYPES.GetSearchExtraParametersProvider,
        )
        values.value.raw = {
          ...values.value.raw,
          ...props.raw,
          ...(typeof extraParamsMapper === 'function'
            ? await extraParamsMapper(values.value)
            : undefined),
        }
      },
      { immediate: true, deep: true },
    )

    return {
      values,
    }
  },
})
